import { z } from "zod";

/**
 * The record of which automated practice-owned steps have already run for one patient.
 *
 * Step completion is derived live from clinical signals, not recorded — so without this, every
 * recompute would re-send a `send-message`/`send-chat` and re-create a `care-team-task`. This is the
 * durable "already fired" marker that makes the executor idempotent. It is the automated-step twin
 * of `step-blockers.ts`: a versioned-JSON blob on one `MemberAccount` extension, parsed with a
 * never-throw fallback.
 *
 * It carries a timestamp per step purely for debugging — "when did the welcome message go out" is a
 * question a support thread asks, and the Account's own version history cannot answer it per step.
 */
const firedWorkflowStepSchema = z.object({
  stepId: z.string().min(1),
  /** ISO timestamp of the run that fired this step. */
  firedAt: z.string().min(1),
});

export type FiredWorkflowStep = z.infer<typeof firedWorkflowStepSchema>;

const firedWorkflowStepsSchema = z.object({
  version: z.literal(1),
  fired: z.array(firedWorkflowStepSchema).max(100),
});

/** What a patient whose workflow has fired no automated step yet has. */
export const NO_FIRED_WORKFLOW_STEPS: readonly FiredWorkflowStep[] = [];

/**
 * Reads the stored blob, falling back to "none fired" on anything unparseable.
 *
 * Never throws. A malformed blob must not take the reconcile down, but note the failure mode here
 * is the dangerous direction: "we could not read the fired set" reads as "nothing has fired", which
 * lets the executor fire again. That is why this record is not the only guard: each firing runs in
 * its own Inngest `step.run` (a finished step replays, it does not re-perform), the care-team task
 * is created with `createIfNoneExist` on a per-step identifier, and the chat is deduped by a per-step
 * correlation id. A malformed blob is not expected in practice, since only the serializer writes it.
 */
export function parseFiredWorkflowSteps(raw: unknown): readonly FiredWorkflowStep[] {
  if (typeof raw !== "string" || !raw) return NO_FIRED_WORKFLOW_STEPS;

  try {
    const result = firedWorkflowStepsSchema.safeParse(JSON.parse(raw));
    return result.success ? result.data.fired : NO_FIRED_WORKFLOW_STEPS;
  } catch {
    return NO_FIRED_WORKFLOW_STEPS;
  }
}

export function serializeFiredWorkflowSteps(fired: readonly FiredWorkflowStep[]): string {
  return JSON.stringify({ version: 1, fired });
}

/** The ids of the steps that have fired, for a quick membership check. */
export function firedWorkflowStepIdSet(fired: readonly FiredWorkflowStep[]): Set<string> {
  return new Set(fired.map((entry) => entry.stepId));
}

/** Adds a step to the fired set, leaving an existing entry for the same step untouched. */
export function addFiredWorkflowStep(
  fired: readonly FiredWorkflowStep[],
  next: FiredWorkflowStep,
): FiredWorkflowStep[] {
  if (fired.some((entry) => entry.stepId === next.stepId)) return [...fired];
  return [...fired, next];
}
